import { ALWAYS_LABELED_TAG_WEB_PATHS, type CityLabelDir, type CityPoint, type SelectableDestination } from '@/wabunka-src/common/features/Featured/core/featuredDestinations/featuredDestinationsMap';

/**
 * 一覧マップの「エリアラベル層」ビューモデル構築（JCAT-5434 / Design Doc §UC-3 F-29〜F-31）。
 *
 * 一覧マップの表示はズームの深さで3段階に切り替わる。
 *
 * | ズーム        | 表示                     | ユーザーが得るもの         |
 * | ------------ | ----------------------- | ----------------------- |
 * | 近い（詳細）    | 体験ピン（サムネ付き）        | 個別の体験                |
 * | 中間          | 件数バッジ（クラスタ）        | どこに体験が集まっているか    |
 * | 広域（閾値以下） | エリアラベル＋ドット（本モジュール）| どんな土地があるか          |
 *
 * 体験ピン⇔件数バッジの切替は `MarkerClusterer` がピクセル距離で自動判定するため、
 * ここで決めるのは「エリアラベル層に入るか」の1点だけ。
 *
 * ドット・ラベルは Google の `AdvancedMarkerElement` ではなく**地図の外側に重ねた自前オーバーレイ**で描く。
 * ベースマップ自身が地名（Tokyo / 東京 / Osaka / 大阪 …）を描いており、その上にマーカーを置くと
 * 地名が二重になって読めなくなるため、地図とオーバーレイの間に半透明のスクリムを挟んで
 * ベースマップを沈める必要がある。マーカーを地図の内部 DOM に置くとスクリムがマーカーにも掛かってしまう。
 * 自前オーバーレイなら重ね順が素直になり、ラベルの位置・向き・タップ領域も自分で決められる。
 *
 * このモジュールは Vue にも Google Maps にも DOM にも依存しない純粋関数として保つ
 * （テストの継ぎ目をここ1箇所に集約する）。実測が要る値（ラベルの文字幅・ペインの寸法）は
 * 呼び出し側が測って渡す。
 */

/** 一覧マップが今どの粒度を描いているか。 */
export type MapMarkerLayer = 'pins' | 'areas';

/**
 * エリアラベル層に入る最大ズーム（これ以下でエリアラベル、これより大きいと従来の体験ピン/クラスタ）。
 *
 * 一覧マップの初期表示は全ピンへの `fitBounds` で Desktop ≒ 6.2 / Mobile ≒ 6.0 と見積られるため、
 * この値だけで判定すると初期表示が層に入り、検索結果ページを開いた瞬間に地図から体験が消える。
 * それは `isInitialView`（下記）で別に防いでいるので、閾値は「日本全体が見えてきたら出す」という
 * 体感で決めてよい。
 */
export const AREA_LAYER_MAX_ZOOM = 6;

/**
 * これ以上は引かせない最小ズーム。日本全体より広い画は用が無く、広げるほど可視範囲が
 * 日付変更線に近づいて投影が壊れる。層の判定（基準の画角の丸め）もこの値を基準にするため、
 * 地図コンポーネントではなくここに置く。
 */
export const AREA_LAYER_MIN_ZOOM = 4;

/** 地図に描く行き先の点（ドット）。ラベルを出すかどうかは配置解決の結果で決まる。 */
export interface AreaMarkerDot {
  /** 行き先タグ webPath（例 'hakone'）。選択状態のキー。 */
  tagWebPath: string;
  /** 表示名（microCMS の titleEn） */
  name: string;
  /** 所属タググループ webPath（GA の area_group に送る） */
  groupWebPath: string;
  latitude: number;
  longitude: number;
  /** ラベルをドットのどちら側に置くかの第一候補（生成スクリプトの手動調整値）。 */
  preferredLabelDir: CityLabelDir;
  /**
   * ラベルを出す優先順位（0 が最優先）。入り切らないラベルはこの順で捨てられる。
   * 選択中を先頭に、以降は地理的な散らばりが最大になる順（farthest-point sampling）。
   */
  labelPriority: number;
}

export interface AreaMarkerLayerViewModel {
  /** 今どの粒度を描くか。'areas' のとき体験ピン・クラスタは隠す。 */
  layer: MapMarkerLayer;
  /** エリアラベル層で描くドット。`layer === 'pins'` のときは常に空。 */
  dots: AreaMarkerDot[];
}

/**
 * どの層かを決める。判断そのものは `mapLayerState` の状態機械が持ち、ここはその結果を写すだけ。
 *
 * **ここでズームから再導出しないこと。** 状態機械はユーザー操作の時点で層を決めるラッチなので、
 * その後に自分で仕掛けた自動フィットが閾値の外へ着地すると、両者の判断が食い違う
 * （層は続いているのに描画だけ体験ピンへ落ち、検索の通知が止まったまま復帰しなくなる）。
 */
export const resolveMapMarkerLayer = (forcePinLayer: boolean): MapMarkerLayer => (forcePinLayer ? 'pins' : 'areas');

/** Web メルカトルの y（緯度を線形補間できる値に直す）。地図への投影に使う。 */
export const mercatorY = (latitude: number): number => {
  // 極付近で発散するため緯度をクランプする（日本の範囲では効かないが、異常値で NaN を返さないため）
  const clamped = Math.max(-85.05112878, Math.min(85.05112878, latitude));
  return Math.log(Math.tan(Math.PI / 4 + (clamped * Math.PI) / 360));
};

/**
 * ラベルを出す優先順位を決める。
 *
 * **farthest-point sampling**（すでに選んだ集合から最も遠い候補を順に採る）。単純に件数順で採ると
 * 東京・京都に寄り、「地方エリアの地名が認識されない」という解決したい課題（要件2）をそのまま
 * 再生産するため、明示的に地理的な散らばりを最大化する。
 *
 * 起点は東京→京都の順で選ぶ。
 * 件数最大のグループから機械的に選ぶと、同一グループ内は webPath 昇順になるため
 * tokyo-and-around で「Tokyo」ではなく「Kamakura」が起点になってしまう（`ALWAYS_LABELED_TAG_WEB_PATHS` を
 * 持つ理由）。どれも無ければ件数最大、それも同数なら webPath 昇順で決める（同じ入力なら常に同じ順になるようにするため）。
 *
 * **基準順は選択に依存させない。** 選択を起点にすると farthest-point sampling の並びが丸ごと
 * 組み替わり、押していない行き先のラベルまで入れ替わる（例: 直島を押すと広島と金沢が消えて
 * 飛騨高山が出る）。地名を覚えてもらうための層で、タップのたびに地名の集合が変わるのは逆効果。
 * 「選択中は必ずラベルを出す」だけを担保するため、**選択中の行き先を基準順の先頭へ昇格**させ、
 * 他の相対順は保つ。他のラベルが消えるのは近接衝突で押し出されるぶんだけになる。
 *
 * ここでは順位を決めるだけで**間引きはしない**。何本入るかはラベルの実寸に依存するため、
 * 実測できる呼び出し側（`resolveAreaLabelPlacement`）が決める。
 */
const orderLabelCandidates = (candidates: { tagWebPath: string; experienceCount: number; longitude: number; latitude: number }[], selectedTagWebPath: string | null): string[] => {
  if (candidates.length === 0) return [];

  // 経度・メルカトル y の差をそのまま距離に使う（順位付けなので単位は問わない。緯度方向の歪みだけ補正する）
  const positionOf = (c: { longitude: number; latitude: number }) => ({ x: c.longitude, y: (mercatorY(c.latitude) * 180) / Math.PI });

  const ordered: typeof candidates = [];
  const remaining = [...candidates];

  const takeAt = (index: number) => {
    const [picked] = remaining.splice(index, 1);
    ordered.push(picked);
  };

  const preferredSeedIndex = ALWAYS_LABELED_TAG_WEB_PATHS.map((tagWebPath) => remaining.findIndex((c) => c.tagWebPath === tagWebPath)).find((index) => index >= 0) ?? -1;
  if (preferredSeedIndex >= 0) {
    takeAt(preferredSeedIndex);
  } else {
    let seedIndex = 0;
    remaining.forEach((c, i) => {
      const best = remaining[seedIndex];
      if (c.experienceCount > best.experienceCount || (c.experienceCount === best.experienceCount && c.tagWebPath < best.tagWebPath)) seedIndex = i;
    });
    takeAt(seedIndex);
  }

  while (remaining.length > 0) {
    let bestIndex = 0;
    let bestDistance = -1;
    remaining.forEach((c, i) => {
      const p = positionOf(c);
      const distance = Math.min(...ordered.map((o) => Math.hypot(positionOf(o).x - p.x, positionOf(o).y - p.y)));
      // 距離が同じなら webPath 昇順で決める（結果を入力順に依存させないため）
      if (distance > bestDistance || (distance === bestDistance && c.tagWebPath < remaining[bestIndex].tagWebPath)) {
        bestDistance = distance;
        bestIndex = i;
      }
    });
    takeAt(bestIndex);
  }

  const baseOrder = ordered.map((c) => c.tagWebPath);
  // 選択中だけを先頭へ昇格させる（他の相対順は変えない）
  if (selectedTagWebPath && baseOrder.includes(selectedTagWebPath)) {
    return [selectedTagWebPath, ...baseOrder.filter((tagWebPath) => tagWebPath !== selectedTagWebPath)];
  }
  return baseOrder;
};

/**
 * エリアラベル層のビューモデルを組み立てる。
 *
 * ドットは「選択できる行き先」（microCMS featured がある行き先タグ）を**全件**出す。
 * ラベルだけを重なり回避で間引くため、行き先がそこに在ることは全件伝わる。
 *
 * 都市点（`destinations.json` の cities）に無い行き先はドットを描く座標が無いため除外する。
 *
 * **層に入るかはズームだけで決める。** データが未着・取得失敗で `dots` が空でも `'areas'` を返す。
 * 「描けるものが無いのにスクリムだけ残る」空白地図を防ぐのは呼び出し側の責務で、画面外の
 * フィルタも呼び出し側にあるため、そこで一緒に見ないと片方の経路が漏れる（実際に漏らした）。
 * 層に入っているかどうかは、ビューポート通知の抑止など**データに依存しない判断**にも使うため、
 * ここでデータ有無を混ぜると「取得中だけ検索結果が差し替わる」といった副作用が出る。
 */
export const buildAreaMarkerLayer = ({
  destinations,
  cityPoints,
  selectedTagWebPath = null,
  forcePinLayer = false,
}: {
  destinations: SelectableDestination[];
  cityPoints: CityPoint[];
  selectedTagWebPath?: string | null;
  /** 体験ピン層に留めるか（初期表示のまま、または基準の画角より引いていない）。 */
  forcePinLayer?: boolean;
}): AreaMarkerLayerViewModel => {
  const layer = resolveMapMarkerLayer(forcePinLayer);
  if (layer === 'pins') return { layer, dots: [] };

  const cityPointByWebPath = new Map(cityPoints.map((p) => [p.webPath, p]));

  const placed = destinations.flatMap((destination) => {
    const point = destination.cityPoint ?? cityPointByWebPath.get(destination.tagWebPath);
    if (!point) return [];
    return [{ destination, point }];
  });

  const priorityOrder = orderLabelCandidates(
    placed.map(({ destination, point }) => ({
      tagWebPath: destination.tagWebPath,
      experienceCount: destination.experienceCount,
      latitude: point.latitude,
      longitude: point.longitude,
    })),
    selectedTagWebPath,
  );

  const dots = placed.map(({ destination, point }) => ({
    tagWebPath: destination.tagWebPath,
    name: destination.name,
    groupWebPath: destination.groupWebPath,
    latitude: point.latitude,
    longitude: point.longitude,
    preferredLabelDir: point.labelDir,
    labelPriority: priorityOrder.indexOf(destination.tagWebPath),
  }));

  return { layer, dots };
};

// ===== 投影（緯度経度 → オーバーレイ上のピクセル）=====

/** 地図の描画範囲。 */
export interface AreaViewport {
  swLat: number;
  swLng: number;
  neLat: number;
  neLng: number;
}

/**
 * 行き先の緯度経度を、オーバーレイ上のピクセルへ投影する。
 *
 * 引きすぎると可視範囲が日付変更線をまたぎ `neLng < swLng` になる。そこで諦めて 0 件を返すと、
 * ドットもラベルも全部消えてスクリムだけの空白地図になるため、**360 度足して正規化し描き続ける**。
 *
 * 画面から大きく外れた行き先は落とす。少しはみ出す程度は残す（ラベルは内側へ折り返るため）。
 */
export const projectAreaDots = ({
  dots,
  viewport,
  pane,
  cullMargin,
}: {
  dots: AreaMarkerDot[];
  viewport: AreaViewport | null;
  pane: { width: number; height: number };
  cullMargin: number;
}): Map<string, { x: number; y: number }> => {
  const positions = new Map<string, { x: number; y: number }>();
  if (!viewport || pane.width === 0 || pane.height === 0) return positions;

  const rawLngSpan = viewport.neLng - viewport.swLng;
  const lngSpan = rawLngSpan > 0 ? rawLngSpan : rawLngSpan + 360;
  const topY = mercatorY(viewport.neLat);
  const ySpan = topY - mercatorY(viewport.swLat);
  // 退化した範囲だけは投影できない（異常値で NaN を撒かないため）
  if (lngSpan <= 0 || ySpan <= 0) return positions;

  for (const dot of dots) {
    const rawDx = dot.longitude - viewport.swLng;
    const x = ((rawDx >= 0 ? rawDx : rawDx + 360) / lngSpan) * pane.width;
    const y = ((topY - mercatorY(dot.latitude)) / ySpan) * pane.height;
    if (x < -cullMargin || x > pane.width + cullMargin || y < -cullMargin || y > pane.height + cullMargin) continue;
    positions.set(dot.tagWebPath, { x, y });
  }
  return positions;
};

// ===== ラベルの配置解決（実測値を注入して重なりを避ける）=====

/** ラベル矩形（オーバーレイ左上を原点とするピクセル）。 */
interface LabelBox {
  left: number;
  right: number;
  top: number;
  bottom: number;
}

/** ラベルどうしの許容間隔。0 だと隣接した文字がくっついて読みにくい。 */
const LABEL_COLLISION_PADDING_PX = 4;
/** 第一候補で入らなかったときに試す順。 */
const LABEL_DIR_CANDIDATES: readonly CityLabelDir[] = ['right', 'left', 'up', 'down'];
/**
 * 当たり判定を縮めるときの下限。N-04 は 44px 以上を求めるが、数 px しか離れていない 2 点に
 * 44px を敷くと片方が構造的に押せなくなる。指で狙える最小限までは縮めることを優先する。
 */
const MIN_HIT_AREA_PX = 20;

/** 点(cx, cy)の四方どこに置くかで決まるラベル矩形。描画側の CSS と一致させること。 */
const labelBoxOf = (dir: CityLabelDir, cx: number, cy: number, width: number, height: number, gap: number): LabelBox => {
  switch (dir) {
    case 'right':
      return { left: cx + gap, right: cx + gap + width, top: cy - height / 2, bottom: cy + height / 2 };
    case 'left':
      return { left: cx - gap - width, right: cx - gap, top: cy - height / 2, bottom: cy + height / 2 };
    case 'up':
      return { left: cx - width / 2, right: cx + width / 2, top: cy - gap - height, bottom: cy - gap };
    case 'down':
      return { left: cx - width / 2, right: cx + width / 2, top: cy + gap, bottom: cy + gap + height };
  }
};

const overlaps = (a: LabelBox, b: LabelBox): boolean =>
  a.left - LABEL_COLLISION_PADDING_PX < b.right && b.left - LABEL_COLLISION_PADDING_PX < a.right && a.top - LABEL_COLLISION_PADDING_PX < b.bottom && b.top - LABEL_COLLISION_PADDING_PX < a.bottom;

/** 配置が決まったラベル。`visible: false` はドットだけ出して文字を出さない。 */
export interface AreaLabelPlacement {
  dir: CityLabelDir;
  visible: boolean;
  /**
   * タップ領域の一辺（px）。既定は `hitAreaSize`（N-04 の 44px）。
   *
   * 広域では行き先どうしが数 px しか離れない（zoom 6 で東京と箱根が約 39px、金沢と飛騨高山が
   * 約 36px）。44px の当たり判定を全件そのまま敷くと矩形が完全に重なり、**後ろに回った行き先は
   * 自分の位置を押しても選べない**。かといって重なる側のボタンを消すと、その行き先は
   * 広域ではまったく選べなくなる。隣との距離まで縮めて、どちらも自分の点の上で押せるようにする。
   */
  hitSize: number;
}

/**
 * ラベルの向きと表示可否を決める。
 *
 * 優先順位（`labelPriority`）の高い順に、第一候補の向きから順に「ペインに収まり、
 * すでに確定したラベルと重ならない」向きを探す。どの向きでも入らなければそのラベルは出さない
 * （ドットは残るのでタップはできる）。
 *
 * ラベルの実寸は呼び出し側が測って `labelSizes` で渡す。文字幅はフォント・端末で変わるため、
 * 座標だけの近似で判定すると入る本数を過小評価してしまう。
 */
export const resolveAreaLabelPlacement = ({
  dots,
  positions,
  labelSizes,
  pane,
  gap,
  hitAreaSize,
  minDotSeparation,
}: {
  dots: AreaMarkerDot[];
  /** ドットの中心座標（オーバーレイ左上を原点とするピクセル）。画面外の行き先は含めない。 */
  positions: Map<string, { x: number; y: number }>;
  /** ラベルの実寸（実測 or canvas 計測）。 */
  labelSizes: Map<string, { width: number; height: number }>;
  pane: { width: number; height: number };
  /** ドット中心からラベルまでの距離。 */
  gap: number;
  /** タップ領域の既定の一辺（N-04 の 44px）。近接時はこれより小さくなる。 */
  hitAreaSize: number;
  /**
   * ラベルを出すドットどうしに最低限空けるピクセル距離。
   *
   * ラベル矩形が重ならなくても（片方を上・片方を下へ逃がせば数値上は重ならない）、
   * ドットが数 px しか離れていなければ2つのラベルが同じ一点を指しているように見えて、
   * どちらがどこを指すのか分からなくなる。広域ほど都市どうしが詰まる（zoom 4 では東京と鎌倉が 5px）
   * ため、矩形の重なりだけでは防げない。
   */
  minDotSeparation: number;
}): Map<string, AreaLabelPlacement> => {
  const placements = new Map<string, AreaLabelPlacement>();
  if (pane.width === 0 || pane.height === 0) return placements;

  const acceptedBoxes: LabelBox[] = [];
  const acceptedDots: { x: number; y: number }[] = [];

  // 当たり判定は**両側とも**最寄りとの距離まで縮める。片側だけ縮めても、縮んだ矩形ごと
  // 相手の 44px 矩形の内側に入るだけで、下位側は自分の点を押しても相手に吸われて選べない。
  // 両側を距離 d に揃えれば、互いの中心が相手の矩形（半幅 d/2）の外に出る。
  const hitSizeOf = (position: { x: number; y: number }, tagWebPath: string): number => {
    let nearest = Number.POSITIVE_INFINITY;
    for (const [otherTagWebPath, other] of positions) {
      if (otherTagWebPath === tagWebPath) continue;
      nearest = Math.min(nearest, Math.hypot(other.x - position.x, other.y - position.y));
    }
    return Math.max(MIN_HIT_AREA_PX, Math.min(hitAreaSize, nearest));
  };

  for (const dot of [...dots].sort((a, b) => a.labelPriority - b.labelPriority)) {
    const position = positions.get(dot.tagWebPath);
    const size = labelSizes.get(dot.tagWebPath);
    if (!position || !size) continue;

    const hitSize = hitSizeOf(position, dot.tagWebPath);

    const tooClose = acceptedDots.some((a) => Math.hypot(a.x - position.x, a.y - position.y) < minDotSeparation);
    const dirs = [dot.preferredLabelDir, ...LABEL_DIR_CANDIDATES.filter((d) => d !== dot.preferredLabelDir)];
    const fitting = tooClose
      ? undefined
      : dirs.find((dir) => {
          const box = labelBoxOf(dir, position.x, position.y, size.width, size.height, gap);
          // ペイン内に収まるかは**判定しない**。パンは平行移動なので、境界で判定すると
          // 地図を動かすたびに向きと可否が入れ替わってラベルがチラつく（spec §4 /  D-008 の
          // 「表示位置に依存せず決める」に反する）。端にかかったラベルは視覚的にクリップされるに任せる。
          return !acceptedBoxes.some((a) => overlaps(a, box));
        });

    if (fitting) {
      acceptedBoxes.push(labelBoxOf(fitting, position.x, position.y, size.width, size.height, gap));
      acceptedDots.push(position);
      placements.set(dot.tagWebPath, { dir: fitting, visible: true, hitSize });
    } else {
      placements.set(dot.tagWebPath, { dir: dot.preferredLabelDir, visible: false, hitSize });
    }
  }

  return placements;
};

/** 当て判定に使う矩形。`DOMRect` をそのまま渡せる形にしてある。 */
export interface AreaHitRect {
  left: number;
  top: number;
  right: number;
  bottom: number;
}

/** 画面上に描かれている 1 つの行き先（ドットと、出ていればラベル）。 */
export interface AreaDotHitCandidate {
  tagWebPath: string;
  /** 前面ほど大きい。同種どうしが重なったときの優先度。 */
  priority: number;
  dotRect: AreaHitRect;
  /** ラベルが出ていなければ null。 */
  labelRect: AreaHitRect | null;
}

const containsPoint = (rect: AreaHitRect, x: number, y: number): boolean => x >= rect.left && x <= rect.right && y >= rect.top && y <= rect.bottom;

/**
 * 画面座標にある行き先を選ぶ。
 *
 * **ドットの矩形を優先し、ラベルの矩形は他に候補が無いときだけ見る。**
 * 当たり判定の縮小（`hitSizeOf`）はドット間の距離しか見ていないため、ラベルを同列に扱うと
 * 隣のラベルに覆われた行き先が選べなくなる（日本全体フィットの画角では東京と箱根が
 * 20px ほどしか離れず、東京のラベルが箱根のドットを覆う）。
 *
 * 同種どうしが重なった場合は前面（`priority` が大きい方）を採り、押せる方と読める方を一致させる。
 */
export const pickAreaDotAt = (candidates: readonly AreaDotHitCandidate[], x: number, y: number): string | null => {
  let dotHit: AreaDotHitCandidate | null = null;
  let labelHit: AreaDotHitCandidate | null = null;

  for (const candidate of candidates) {
    if (containsPoint(candidate.dotRect, x, y)) {
      if (dotHit == null || candidate.priority > dotHit.priority) dotHit = candidate;
      continue;
    }
    if (candidate.labelRect == null || !containsPoint(candidate.labelRect, x, y)) continue;
    if (labelHit == null || candidate.priority > labelHit.priority) labelHit = candidate;
  }

  return (dotHit ?? labelHit)?.tagWebPath ?? null;
};
